import {
  Box,
  Divider,
  Flex,
  Text,
  Heading,
  Spinner,
  Input,
  Grid,
  GridItem,
  Button,
  HStack,
  useToast,
  Select as SelectBase,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { Header } from '../../components/Header';
import ReactTableComponent from '../../components/TableComponent';
import api from '../../service/api';
import { getColumn } from '../../utils/getOrderColumn';
import { entradaDoPedido } from '../../helpers/entradaPedido';
import { useHistory } from 'react-router-dom';
import { SiderbarResponsive } from '../../components/SiderbarResponsive';
import { Wapper } from '../../components/Wapper';
import OrderInfoModal from '../../components/OrderInfoModal';
import { useAuth } from '../../context/AuthContext';
import { aprovarPedido } from '../../service/aprovacaoPedido';
import {
  integrarPedidosPendentes,
  podeIntegrarPedidos,
  RECARGAS_APOS_INTEGRAR_MS,
} from '../../service/integracaoPedidos';
import { podeAprovarPedido } from '../../utils/podeAprovarPedido';
import { InputCustom } from '../../components/InputCustom/InputCustom';
import { useForm } from 'react-hook-form';
import {
  codigoSituacao,
  opcoesDeSituacao,
  rotuloSituacao,
} from '../../utils/situacaoPedido';
import { useFiltroDaSessao } from '../../hooks/useFiltroDaSessao';

// Cast para evitar o TS2590 ("union type too complex") do Chakra.
const Select = SelectBase as React.ComponentType<any>;

interface IPeriodo {
  inicio: string;
  fim: string;
}

// O dia é o da entrada do pedido no horário local (entradaDoPedido): o
// pedido do app feito depois das 21h não cai no dia seguinte.
function pedidoNoPeriodo(o: any, { inicio, fim }: IPeriodo) {
  const entrada = entradaDoPedido(o.data_emissao, o.hora_emissao)?.momento;
  return (
    !!entrada &&
    entrada >= DateTime.fromISO(inicio).startOf('day') &&
    entrada <= DateTime.fromISO(fim).endOf('day')
  );
}

export default function ListOrder() {
  const {
    register,
    handleSubmit,
    formState,
    reset,
    control,
    setValue,
    getValues,
  } = useForm();
  const { errors } = formState;
  const [orders, setOrders] = useState<any[]>([]);
  const [showIndex, setShowIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  // O link do pedido recarrega a página: sem guardar na sessão, quem abria um
  // pedido e voltava perdia o período, a situação e os filtros da tabela.
  const chaveFiltro = (nome: string) =>
    `@Aplication:pedidos:filtro:${user?.id ?? ''}:${nome}`;
  // Período da última pesquisa; null = sem filtro de data.
  const [periodo, setPeriodo] = useFiltroDaSessao<IPeriodo | null>(
    chaveFiltro('periodo'),
    null
  );
  const [initialDate, setInitialDate] = useState(periodo?.inicio ?? '');
  const [finalDate, setFinalDate] = useState(periodo?.fim ?? '');
  const [params, setParams] = useState<any[]>([]);
  const [reload, setReload] = useState(0);
  const [situacaoFiltro, setSituacaoFiltro] = useFiltroDaSessao(
    chaveFiltro('situacao'),
    ''
  );
  const [integrando, setIntegrando] = useState(false);
  const recargasAposIntegrar = useRef<ReturnType<typeof setTimeout>[]>([]);

  const history = useHistory();

  const toast = useToast();

  // Gerentes (qualquer variação: regional, nacional, etc.) e o admin podem aprovar.
  const isGerente = podeAprovarPedido(user?.role);
  const podeIntegrar = podeIntegrarPedidos(user?.role);

  useEffect(() => {
    async function getParams() {
      try {
        const response = await api.get(
          `/api-essencial/v1/parametrizacao/${user.empresa.id}`
        );

        setValue(
          'percentualAprovacaoPedido',
          response.data[0].percentual_aprovacao_pedido
        );
        setParams(response.data);
      } catch (error) {}
    }
    getParams();

    async function getPedidos() {
      try {
        const response = await api.get(
          `/api-essencial/v1/pedidos/${user.empresa.id}/empresa`
        );

        // const orderParams = response.data.data;
        // if (params[0].percentualAprovacaoPedido == undefined) return;
        // const orderParams = response.data.data.filter(
        //   (o: any) => o.margemPedido <= params[0].percentualAprovacaoPedido
        // );

        // Prioridade na lista (mais alta primeiro):
        //   3 = pedido do app aguardando aprovação (EM_ANALISE) — precisa de ação;
        //   2 = qualquer pedido feito pelo app pelos vendedores (tem "payload_enviado"
        //       com a assinatura "INTEGRACAO APP ESF" OU "criado_por_id" preenchido).
        //       O backend nem sempre devolve payload_enviado (vem nulo), então usamos
        //       criado_por_id como sinal adicional — esse campo só existe em pedido do app;
        //   1 = em análise que não veio do app (raro);
        //   0 = demais pedidos (vindos do TOTVS).
        // Dentro de cada grupo, ordena do mais recente para o mais antigo.
        const feitoPeloApp = (o: any) => !!o?.payload_enviado || !!o?.criado_por_id;
        const emAnalise = (o: any) =>
          String(o?.situacao || '').toUpperCase() === 'EM_ANALISE';
        const prioridade = (o: any) => {
          if (feitoPeloApp(o) && emAnalise(o)) return 3;
          if (feitoPeloApp(o)) return 2;
          if (emAnalise(o)) return 1;
          return 0;
        };
        const dataPedido = (o: any) =>
          DateTime.fromISO(o?.ultima_alteracao ?? o?.data_emissao).toMillis();

        const pedidosOrdenados = [...(response.data ?? [])].sort((a: any, b: any) => {
          const pa = prioridade(a);
          const pb = prioridade(b);
          if (pa !== pb) return pb - pa; // maior prioridade primeiro
          return dataPedido(b) - dataPedido(a); // mais recente primeiro
        });

        setOrders(pedidosOrdenados);
      } catch (error) {}

      setIsLoading(false);
    }
    getPedidos();
  }, [orders.length, user.empresa.id, setValue, reload]);

  useEffect(() => {
    const recargas = recargasAposIntegrar;
    return () => recargas.current.forEach(clearTimeout);
  }, []);

  function showModal(index: number) {
    setShowIndex(index);
  }

  function handleInitialDate(e: any) {
    setInitialDate(e.target?.value);
  }

  function handleFinalDate(e: any) {
    setFinalDate(e.target?.value);
  }

  function handleReset() {
    setInitialDate('');
    setFinalDate('');
    setPeriodo(null);
    setSituacaoFiltro('');
  }

  function handlePercentAprovaded() {
    // if (params[0].percentualAprovacaoPedido == undefined) return;
    // if (initialDate == "" || finalDate == "") {
    //   const orderFilter = orders.filter(
    //     (o: any) => o.margemPedido <= params[0].percentualAprovacaoPedido
    //   );
    // }
  }

  function handleSearch() {
    if (DateTime.fromISO(finalDate) < DateTime.fromISO(initialDate)) {
      return toast({
        title: 'DATAS INVÁLIDAS',
        description: 'A data inicial é maior que a data Final',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }

    // if (
    //   params[0].percentualAprovacaoPedido != "" &&
    //   initialDate === "" &&
    //   initialDate === ""
    // ) {
    //   const orderFilter = orders.filter(
    //     (o) => o.margemPedido <= params[0].percentualAprovacaoPedido
    //   );

    //   setOrderFilter(orderFilter);
    //   return;
    // }

    // A lista do período sai de pedidoNoPeriodo, recalculada a cada recarga
    // (antes era uma cópia tirada na hora da pesquisa e ficava desatualizada).
    setPeriodo({ inicio: initialDate, fim: finalDate });

    // if (orderFilter.length === 0) {
    //   return toast({
    //     title: "SEM PEDIDOS",
    //     description: "Não há pedidos nesse período",
    //     status: "error",
    //     duration: 3000,
    //     isClosable: true,
    //   });
    // }
  }

  async function handleApprove(id: string) {
    try {
      await aprovarPedido(id, user?.name);
      toast({
        title: 'PEDIDO APROVADO',
        description: 'O pedido foi liberado e será integrado ao ERP.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setReload((r) => r + 1);
    } catch (error) {
      toast({
        title: 'ERRO AO APROVAR',
        description: 'Não foi possível aprovar o pedido. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  // Manda agora ao ERP os pedidos liberados que ainda não foram (e os que
  // voltaram com erro), sem esperar a rodada automática de 15 em 15 min.
  async function handleIntegrar() {
    setIntegrando(true);
    try {
      await integrarPedidosPendentes();
      toast({
        title: 'INTEGRAÇÃO INICIADA',
        description:
          'Os pedidos pendentes estão sendo enviados ao ERP. A lista atualiza sozinha em instantes.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      recargasAposIntegrar.current.forEach(clearTimeout);
      recargasAposIntegrar.current = RECARGAS_APOS_INTEGRAR_MS.map((espera) =>
        setTimeout(() => setReload((r) => r + 1), espera)
      );
    } catch (error: any) {
      toast({
        title: 'ERRO AO INTEGRAR',
        description:
          error?.response?.data?.message ||
          'Não foi possível iniciar a integração. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIntegrando(false);
    }
  }

  const pedidosDoPeriodo = periodo
    ? orders.filter((o) => pedidoNoPeriodo(o, periodo))
    : orders;
  // Dá para isolar na lista os pedidos de uma situação só (em análise para
  // aprovar, erro de integração para corrigir e reenviar, etc.).
  const situacoes = opcoesDeSituacao(pedidosDoPeriodo, situacaoFiltro);
  const pedidosExibidos = situacaoFiltro
    ? pedidosDoPeriodo.filter((o: any) => codigoSituacao(o) === situacaoFiltro)
    : pedidosDoPeriodo;

  const column = getColumn(
    () => {},
    '/listar/pedido',
    showModal,
    handleApprove,
    isGerente
  );

  return (
    <>
      <Header />
      <SiderbarResponsive />

      <Flex align="start" mt="8" px="6">
        <Wapper>
          <Box flex="1" p="8" bg="gray.800" borderRadius={8} mb="16">
            <Flex justify="space-between" align="center">
              <Heading size="md" fontWeight="normal">
                LISTA DE PEDIDOS
              </Heading>
              {podeIntegrar && (
                <Button
                  size="sm"
                  fontSize="sm"
                  bg="green.500"
                  color="white"
                  _hover={{ bg: 'green.700' }}
                  _focus={{ boxShadow: 'none' }}
                  isLoading={integrando}
                  loadingText="Enviando..."
                  onClick={handleIntegrar}
                  title="Envia agora ao ERP os pedidos pendentes, sem esperar a integração automática"
                >
                  Integrar pedidos
                </Button>
              )}
            </Flex>

            <Divider my="6" borderColor="gray.700" />

            <Grid
              width="100%"
              templateColumns="repeat(5, 1fr)"
              gap={4}
              // minChildWidth="120px"
            >
              {/* <Box>
                <InputCustom
                  name="pedido"
                  label="Pedido"
                  errors={errors}
                  register={register}
                />
              </Box> */}
              <Box>
                <InputCustom
                  name="inicio"
                  label="Data Inicial"
                  errors={errors}
                  control={control}
                  value={initialDate}
                  onChange={handleInitialDate}
                  type="date"
                />
              </Box>
              <Box>
                <InputCustom
                  name="final"
                  label="Data Final"
                  errors={errors}
                  control={control}
                  value={finalDate}
                  onChange={handleFinalDate}
                  type="date"
                />
              </Box>
              <Box>
                <InputCustom
                  name="percentualAprovacaoPedido"
                  label="% Aprovação Pedido"
                  errors={errors}
                  register={register}
                  isDisabled
                  // value={percentual_aprovacao_pedido}
                  onChange={handlePercentAprovaded}
                />
              </Box>
              <Box>
                {/* Mesmo FormControl/FormLabel e tamanho padrão do InputCustom,
                    senão o rótulo e a caixa ficam desalinhados das datas. */}
                <FormControl>
                  <FormLabel htmlFor="filtro-situacao">Situação</FormLabel>
                  <Select
                    id="filtro-situacao"
                    value={situacaoFiltro}
                    onChange={(e: any) => setSituacaoFiltro(e.target.value)}
                    bgColor="gray.700"
                    variant="filled"
                    _hover={{ bgColor: 'gray.700' }}
                  >
                    <option value="">Todas as situações</option>
                    {situacoes.map((s) => (
                      <option key={s.codigo} value={s.codigo}>
                        {s.rotulo} ({s.total})
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Flex mt={8} flexDir="row">
                <Button
                  onClick={handleSearch}
                  colorScheme="orange"
                  _focus={{ boxShadow: 'none' }}
                >
                  Pesquisar
                </Button>
                <Button
                  colorScheme="orange"
                  ml={4}
                  onClick={handleReset}
                  _focus={{ boxShadow: 'none' }}
                >
                  Limpar
                </Button>
              </Flex>
            </Grid>

            <Divider my="6" borderColor="gray.700" />

            <Flex justifyContent="center">
              {isLoading ? (
                <Spinner color="white" />
              ) : (
                <>
                  {pedidosExibidos.length === 0 ? (
                    <Flex>
                      <Text color="orange.200">
                        {situacaoFiltro
                          ? `Nenhum pedido na situação "${rotuloSituacao(situacaoFiltro)}"`
                          : 'Sem pedidos para exibir'}
                      </Text>
                    </Flex>
                  ) : (
                    <>
                      <ReactTableComponent
                        columns={column}
                        data={pedidosExibidos}
                        isPagenable
                        storageKey={chaveFiltro('tabela')}
                      />

                      <OrderInfoModal
                        order={orders}
                        showIndex={showIndex}
                        setShowIndex={setShowIndex}
                      />
                    </>
                  )}
                </>
              )}
            </Flex>
          </Box>
        </Wapper>
      </Flex>
    </>
  );
}
